import { createApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { AxiosRequestConfig, AxiosError } from 'axios';
import axiosInstance from 'api/axios';

const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      headers?: AxiosRequestConfig['headers'];
    },
    unknown,
    unknown
  > =>
  async ({ url, method = 'GET', data, params, headers }) => {
    try {
      const result = await axiosInstance({ url, method, data, params, headers });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const gamesApi = createApi({
  reducerPath: 'gamesApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['RecentlyPlayed', 'Collections'],
  keepUnusedDataFor: 300, // 5 minutes caching
  endpoints: (builder) => ({
    getHomeGames: builder.query<any, void>({
      query: () => {
        const siteId = process.env.REACT_APP_TENANT_ID || '1';
        return {
          url: `/client/games/home?siteId=${siteId}`,
          method: 'GET',
        };
      },
    }),
    getCategoryGames: builder.query<
      any,
      {
        slug: string;
        page?: number;
        pageSize?: number;
        search?: string;
        sortBy?: string;
        providerIds?: number[];
        gameType?: string;
        lang?: string;
      }
    >({
      query: ({ slug, page = 1, pageSize = 16, search, sortBy, providerIds, gameType, lang }) => {
        const siteId = process.env.REACT_APP_TENANT_ID || '1';
        const params: Record<string, any> = {
          page,
          pageSize,
          siteId,
        };
        if (search) params.search = search;
        if (sortBy) params.sortBy = sortBy;
        if (providerIds && providerIds.length > 0) params.providers = providerIds;
        if (gameType) params.gameType = gameType;

        return {
          url: `/client/games/categories/${slug}/games`,
          method: 'GET',
          params,
          headers: lang ? { 'x-language': lang } : undefined,
        };
      },
    }),
    getCategoryProviders: builder.query<
      any,
      {
        slug: string;
        gameType?: string;
      }
    >({
      query: ({ slug, gameType }) => {
        const siteId = process.env.REACT_APP_TENANT_ID || '1';
        const params: Record<string, any> = { siteId };
        if (gameType) params.gameType = gameType;

        return {
          url: `/client/games/categories/${slug}/providers`,
          method: 'GET',
          params,
        };
      },
    }),
    getCollections: builder.query<any, string | void>({
      query: (lang) => {
        const siteId = process.env.REACT_APP_TENANT_ID || '1';
        return {
          url: '/client/games/collections',
          method: 'GET',
          params: { siteId },
          headers: lang ? { 'x-language': lang } : undefined,
        };
      },
      providesTags: ['Collections'],
      keepUnusedDataFor: 300, // 5 minutes caching per language
    }),
    getRecentlyPlayed: builder.query<any, void>({
      query: () => {
        const siteId = process.env.REACT_APP_TENANT_ID || '1';
        return {
          url: '/client/games/recently-played',
          method: 'GET',
          params: { siteId },
        };
      },
      providesTags: ['RecentlyPlayed'],
    }),
    getSubProviders: builder.query<any, { page?: number; limit?: number; search?: string } | void>({
      query: (arg) => {
        const siteId = process.env.REACT_APP_TENANT_ID || '1';
        const params: Record<string, any> = { siteId };
        if (arg) {
          if (arg.page) params.page = arg.page;
          if (arg.limit) params.limit = arg.limit;
          if (arg.search && arg.search.trim()) params.search = arg.search.trim();
        }
        return {
          url: '/client/games/sub-providers',
          method: 'GET',
          params,
        };
      },
    }),
    getSubProviderByName: builder.query<any, string>({
      query: (name) => {
        const siteId = process.env.REACT_APP_TENANT_ID || '1';
        return {
          url: `/client/games/sub-providers/${name}`,
          method: 'GET',
          params: { siteId },
        };
      },
      keepUnusedDataFor: 3600,
    }),
    getGamesBySubProvider: builder.query<
      any,
      {
        subProviderName: string;
        page?: number;
        limit?: number;
        search?: string;
        sortBy?: string;
      }
    >({
      query: ({ subProviderName, page = 1, limit = 16, search, sortBy }) => {
        const siteId = process.env.REACT_APP_TENANT_ID || '1';
        const params: Record<string, any> = {
          page,
          limit,
          siteId,
          subProviderName,
        };
        if (search) params.search = search;
        if (sortBy) params.sortBy = sortBy;

        return {
          url: `/client/games/by-sub-provider`,
          method: 'GET',
          params,
        };
      },
    }),
    getWinners: builder.query<any, void>({
      query: () => {
        const siteId = process.env.REACT_APP_TENANT_ID || '1';
        return {
          url: '/client/games/winners',
          method: 'GET',
          params: { siteId },
        };
      },
      keepUnusedDataFor: 300,
    }),
    getAllBets: builder.query<any, { page: number; limit: number }>({
      query: ({ page, limit }) => {
        const siteId = process.env.REACT_APP_TENANT_ID || '1';
        return {
          url: '/client/games/bets',
          method: 'GET',
          params: { page, limit, siteId },
        };
      },
      keepUnusedDataFor: 60,
    }),
    getMyBets: builder.query<any, { page: number; limit: number }>({
      query: ({ page, limit }) => ({
        url: '/client/games/bets/my',
        method: 'GET',
        params: { page, limit },
      }),
      keepUnusedDataFor: 60,
    }),
    getMyBetsStats: builder.query<any, void>({
      query: () => ({
        url: '/client/games/bets/my/stats',
        method: 'GET',
      }),
      keepUnusedDataFor: 60,
    }),
    getNotifications: builder.query<any, { page: number; limit: number }>({
      query: ({ page, limit }) => ({
        url: '/client/notifications',
        method: 'GET',
        params: { page, limit },
      }),
      keepUnusedDataFor: 0,
    }),
    viewNotification: builder.mutation<any, number>({
      query: (id) => ({
        url: `/client/notifications/${id}/view`,
        method: 'POST',
      }),
    }),
    getBetRound: builder.query<any, string | number>({
      query: (roundIdCasino) => ({
        url: `/client/games/round/${roundIdCasino}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 60,
    }),
    getSportsbookBets: builder.query<any, { page?: number; limit?: number } | void>({
      query: (params) => ({
        url: '/client/sportsbook/bets',
        method: 'GET',
        params: params || undefined,
      }),
      keepUnusedDataFor: 60,
    }),
  }),
});

export const {
  useGetHomeGamesQuery,
  useGetCategoryGamesQuery,
  useGetCategoryProvidersQuery,
  useGetCollectionsQuery,
  useGetRecentlyPlayedQuery,
  useGetSubProvidersQuery,
  useGetSubProviderByNameQuery,
  useGetGamesBySubProviderQuery,
  useGetWinnersQuery,
  useGetAllBetsQuery,
  useGetMyBetsQuery,
  useGetMyBetsStatsQuery,
  useGetSportsbookBetsQuery,
  useGetNotificationsQuery,
  useViewNotificationMutation,
  useGetBetRoundQuery,
  useLazyGetBetRoundQuery,
} = gamesApi;
